import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSub } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, SpinnerGap } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const nav = useNavigate();
  const { refresh } = useSub();
  const [state, setState] = useState("checking"); // checking | success | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      if (attempts.current >= 6) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "completed") {
          refresh();
          setState("success");
          return;
        }
      } catch (e) {}
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [refresh]);

  return (
    <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="payment-success">
      {state === "checking" && (<>
        <SpinnerGap size={48} className="text-volt animate-spin" />
        <h1 className="mt-5 font-heading text-2xl font-bold">Confirming your subscription…</h1>
        <p className="mt-2 text-sm text-muted-foreground">Hang tight while we activate your plan.</p>
      </>)}
      {state === "success" && (<>
        <CheckCircle size={56} weight="fill" className="text-signal-green" />
        <h1 className="mt-5 font-heading text-3xl font-bold">You're all set!</h1>
        <p className="mt-2 text-sm text-muted-foreground">Your 7-day trial has started and premium features are unlocked.</p>
        <div className="mt-6 flex gap-3">
          <Button data-testid="go-dashboard" onClick={() => nav("/")} className="bg-volt hover:bg-volt/90 rounded-sm">Go to Dashboard</Button>
          <Button variant="outline" onClick={() => nav("/scanner")} className="rounded-sm border-border">Try the Scanner</Button>
        </div>
      </>)}
      {(state === "timeout" || state === "error") && (<>
        <XCircle size={48} className="text-signal-yellow" />
        <h1 className="mt-5 font-heading text-2xl font-bold">Still processing</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-md">Your payment may take a moment to confirm. Refresh your account page shortly — if the plan doesn't activate, contact support.</p>
        <Button onClick={() => nav("/account")} className="mt-6 bg-volt hover:bg-volt/90 rounded-sm">Go to Account</Button>
      </>)}
    </div>
  );
}
